<script setup lang="ts">
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';
  import { useId, computed } from 'vue';

  interface Option {
    label: string,
    value: any,
  }

  interface Props {
    id?: string | undefined;
    name?: string | undefined;
    label?: string | undefined;
    options?: Option[];
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<Props>(), {
    required: false,
    readonly: false,
    disabled: false,
    options: () => [],
  });


  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const disabled = computed(() => {
    // Checkboxes may have only a disabled state, for compatibility we treat a readonly as disabled here.
    return Boolean(props.disabled || props.readonly || (context && context.readonly.value));
  });
</script>
<template>
  <SfControlWrapper class="control--radio" v-bind="{ id, name,  required }" v-slot="{errorId}">
    <fieldset :aria-describedby="errorId">
      <legend>{{ label }}</legend>
      <label class="radio-container" v-for="option in options" :for="id + '-' + option.value">
        <input type="radio" class="radio-input" :id="id + '-' + option.value" :value="option.value" v-bind="{ name, disabled, ...$attrs }" v-model="model" />
        <span class="checkmark" />
        <span class="radio-label" v-html="option.label" />
      </label>
    </fieldset>
  </SfControlWrapper>
</template>
<style lang="scss">
  .control--radio {

    fieldset {
      border: 0;
      min-width: 0;
    }

    legend {
      display: block;
    }

    .label {
      position: static;
      padding: 0 0 0.25rem 0;
    }

    .radio-container {
      display: block;
      position: relative;
      cursor: pointer;
      user-select: none;
      padding-left: calc(var(--sf-radio-line-height) * 1.5);
      line-height: var(--sf-radio-line-height);
      margin: calc(var(--sf-radio-line-height) / 2) 0;

      .radio-input {
        position: absolute;
        opacity: 0;
        height: 0;
        width: 0;

        &:focus-visible ~ .checkmark {
          background-color: var(--sf-input-background-focus);
          border-color: var(--sf-input-border-focus);
          outline: 2px solid var(--sf-input-border-focus);
        }

        &:active ~ .checkmark {
          background-color: var(--sf-input-background-active);
          border-color: var(--sf-input-border-active);
        }

        &:checked ~ .checkmark:after {
          display: block;
        }

        &:disabled {
          & ~ .checkmark {
            background-color: var(--sf-input-background-disabled);
            border-color: var(--sf-input-border-disabled);
            pointer-events: none;
            cursor: not-allowed;

            &:after {
              background-color: var(--sf-input-border-disabled);
            }
          }

          & ~ .radio-label {
            color: var(--sf-input-color-disabled);
            pointer-events: none;
            cursor: not-allowed;
          }
        }
      }

      .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        padding: 0;
        height: var(--sf-radio-line-height);
        width: var(--sf-radio-line-height);
        border-radius: 50%;
        border: 1px solid var(--sf-radio-checkmark-border-color);

        &:after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          display: none;
          transform: translate(-50%, -50%);
          height: calc(var(--sf-radio-line-height) - 6px);
          width: calc(var(--sf-radio-line-height) - 6px);
          border-radius: 50%;
          background-color: var(--sf-radio-checkmark-background-color);
        }
      }

      .radio-label {
        font-size: var(--sf-radio-label-font-size);
        text-transform: var(--sf-radio-label-text-transform);
      }
    }
  }
</style>
